import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Table, Accordion, Alert, Spinner, Modal } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import Cookies from 'js-cookie';
import { COUNTRY_DIAL_CODES, findByCode, validatePhone, stripNonDigits } from '../utils/countryDialCodes';

const BlockRepository = ({ ViewedPage, shop_domain, app_url, isInSettingsContainer }) => {
  // Call ViewedPage only once when component mounts
  useEffect(() => {
    ViewedPage(33); // New page ID for Block Repository
  }, []); // Empty dependency array to call only once

  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [formData, setFormData] = useState({
    type: 'Video',
    title: '',
    description: '',
    url: '',
    country_code: 'US',
    // Contact-type fields: phone and whatsapp are independent (each carries its
    // own country/dial code). At least one must be filled. Legacy `url` and
    // `country_code` above mirror the phone side so older readers keep working.
    phone_country_code: 'US',
    phone_number: '',
    whatsapp_country_code: 'US',
    whatsapp_number: ''
  });
  const [file, setFile] = useState(null);
  const [imageInputType, setImageInputType] = useState('upload'); // 'upload' or 'url'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [filterType, setFilterType] = useState('All');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [urlValidationError, setUrlValidationError] = useState('');
  const [isValidatingVideoUrl, setIsValidatingVideoUrl] = useState(false);
  const [videoUrlValidationError, setVideoUrlValidationError] = useState('');
  const [activeKeys, setActiveKeys] = useState(['add-assets']);

  // Asset type options
  const assetTypes = [
    { value: 'PDF', label: 'PDF' },
    { value: 'Video', label: 'Video' },
    { value: 'Image', label: 'Image' },
    { value: 'Contact', label: 'Contact' },
    { value: 'Get Directions', label: 'Get Directions' }
  ];

  // Load all assets from server
  const loadAssets = async () => {
    try {
      setLoading(true);
      console.log('Loading assets for shop_domain:', shop_domain);
      const response = await axios.post(`${app_url}/api/assets/get_all`, {
        shop_domain: shop_domain
      });
      
      console.log('Assets API response:', response.data);
      if (response.data.success) {
        setAssets(response.data.data || []);
        console.log('Assets loaded:', response.data.data?.length || 0, 'items');
      } else {
        console.error('API returned error:', response.data.message);
        toast.error(response.data.message || 'Failed to load assets');
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Error loading assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load assets on component mount
  useEffect(() => {
    if (shop_domain && app_url) {
      loadAssets();
    }
  }, [shop_domain, app_url]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle URL input for Link and Contact types
  const handleUrlChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      url: value
    }));
    
    // Trigger real-time validation for image URLs
    if (formData.type === 'Image' && imageInputType === 'url') {
      validateImageUrlRealTime(value);
    }
    
    // Trigger real-time validation for video URLs
    if (formData.type === 'Video') {
      validateVideoUrlRealTime(value);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // File size validation (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        toast.error(`File size too large. Maximum allowed size is 10MB. Your file is ${formatFileSize(selectedFile.size)}.`);
        e.target.value = ''; // Clear the file input
        setFile(null);
        return;
      }
      
      // Validate file type based on selected asset type
      const fileType = selectedFile.type;
      const assetType = formData.type.toLowerCase();
      
      if (assetType === 'pdf' && !fileType.includes('pdf')) {
        toast.error('Please select a PDF file');
        e.target.value = ''; // Clear the file input
        return;
      }
      if (assetType === 'image' && !fileType.startsWith('image/')) {
        toast.error('Please select an image file');
        e.target.value = ''; // Clear the file input
        return;
      }
      if (assetType === 'audio' && !fileType.startsWith('audio/')) {
        toast.error('Please select an audio file');
        e.target.value = ''; // Clear the file input
        return;
      }
      
      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };

  // Handle image input type change
  const handleImageInputTypeChange = (type) => {
    setImageInputType(type);
    setFile(null); // Clear file when switching
    setUrlValidationError(''); // Clear validation error
    setIsValidatingUrl(false); // Clear validation state
    if (type === 'url') {
      setFormData(prev => ({ ...prev, url: '' }));
    }
  };

  // URL validation functions
  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      
      // Block dangerous protocols
      const dangerousProtocols = [
        'javascript:',
        'data:',
        'vbscript:',
        'file:',
        'ftp:',
        'blob:',
        'about:',
        'chrome:',
        'chrome-extension:',
        'moz-extension:',
        'ms-appx:',
        'ms-appx-web:'
      ];
      
      const protocol = url.protocol.toLowerCase();
      if (dangerousProtocols.includes(protocol)) {
        return false;
      }
      
      // Only allow http and https protocols
      if (protocol !== 'http:' && protocol !== 'https:') {
        return false;
      }
      
      return true;
    } catch (_) {
      return false;
    }
  };

  const isValidImageUrl = async (url) => {
    if (!isValidUrl(url)) return false;
    
    // Security check: Block script injection patterns
    const dangerousPatterns = [
      '<script',
      '</script>',
      'javascript:',
      'vbscript:',
      'onload=',
      'onerror=',
      'onclick=',
      'onmouseover=',
      'onfocus=',
      'onblur=',
      'onchange=',
      'onsubmit=',
      'onreset=',
      'onselect=',
      'onkeydown=',
      'onkeyup=',
      'onkeypress=',
      'onmousedown=',
      'onmouseup=',
      'onmousemove=',
      'onmouseout=',
      'onmouseover=',
      'onmouseenter=',
      'onmouseleave=',
      'oncontextmenu=',
      'onresize=',
      'onscroll=',
      'onunload=',
      'onbeforeunload=',
      'onabort=',
      'oncanplay=',
      'oncanplaythrough=',
      'ondurationchange=',
      'onemptied=',
      'onended=',
      'onerror=',
      'onloadeddata=',
      'onloadedmetadata=',
      'onloadstart=',
      'onpause=',
      'onplay=',
      'onplaying=',
      'onprogress=',
      'onratechange=',
      'onseeked=',
      'onseeking=',
      'onstalled=',
      'onsuspend=',
      'ontimeupdate=',
      'onvolumechange=',
      'onwaiting=',
      'eval(',
      'Function(',
      'setTimeout(',
      'setInterval(',
      'document.',
      'window.',
      'alert(',
      'confirm(',
      'prompt(',
      'console.',
      'localStorage.',
      'sessionStorage.',
      'cookie',
      'document.cookie',
      'innerHTML',
      'outerHTML',
      'insertAdjacentHTML',
      'createElement',
      'appendChild',
      'removeChild',
      'replaceChild',
      'insertBefore',
      'cloneNode',
      'importNode',
      'adoptNode',
      'normalize',
      'splitText',
      'deleteData',
      'insertData',
      'replaceData',
      'substringData',
      'getAttribute',
      'setAttribute',
      'removeAttribute',
      'hasAttribute',
      'getAttributeNode',
      'setAttributeNode',
      'removeAttributeNode',
      'getElementsByTagName',
      'getElementsByClassName',
      'getElementById',
      'querySelector',
      'querySelectorAll',
      'getComputedStyle',
      'matchMedia',
      'addEventListener',
      'removeEventListener',
      'dispatchEvent',
      'createEvent',
      'createEventObject',
      'attachEvent',
      'detachEvent',
      'fireEvent',
      'execCommand',
      'queryCommandEnabled',
      'queryCommandIndeterm',
      'queryCommandState',
      'queryCommandSupported',
      'queryCommandValue',
      'write(',
      'writeln(',
      'open(',
      'close(',
      'clear(',
      'focus(',
      'blur(',
      'print(',
      'stop(',
      'forward(',
      'back(',
      'home(',
      'search(',
      'reload(',
      'replace(',
      'assign(',
      'navigate(',
      'go(',
      'pushState(',
      'replaceState(',
      'popstate',
      'hashchange',
      'beforeunload',
      'unload',
      'load',
      'error',
      'abort',
      'resize',
      'scroll',
      'focus',
      'blur',
      'change',
      'submit',
      'reset',
      'select',
      'keydown',
      'keyup',
      'keypress',
      'mousedown',
      'mouseup',
      'mousemove',
      'mouseout',
      'mouseover',
      'mouseenter',
      'mouseleave',
      'contextmenu',
      'dblclick',
      'click',
      'drag',
      'dragend',
      'dragenter',
      'dragexit',
      'dragleave',
      'dragover',
      'dragstart',
      'drop',
      'touchstart',
      'touchend',
      'touchmove',
      'touchcancel',
      'gesturestart',
      'gesturechange',
      'gestureend',
      'orientationchange',
      'devicemotion',
      'deviceorientation',
      'deviceproximity',
      'userproximity',
      'visibilitychange',
      'storage',
      'message',
      'messageerror',
      'beforeprint',
      'afterprint',
      'beforeunload',
      'unload',
      'load',
      'error',
      'abort',
      'resize',
      'scroll',
      'focus',
      'blur',
      'change',
      'submit',
      'reset',
      'select',
      'keydown',
      'keyup',
      'keypress',
      'mousedown',
      'mouseup',
      'mousemove',
      'mouseout',
      'mouseover',
      'mouseenter',
      'mouseleave',
      'contextmenu',
      'dblclick',
      'click',
      'drag',
      'dragend',
      'dragenter',
      'dragexit',
      'dragleave',
      'dragover',
      'dragstart',
      'drop',
      'touchstart',
      'touchend',
      'touchmove',
      'touchcancel',
      'gesturestart',
      'gesturechange',
      'gestureend',
      'orientationchange',
      'devicemotion',
      'deviceorientation',
      'deviceproximity',
      'userproximity',
      'visibilitychange',
      'storage',
      'message',
      'messageerror',
      'beforeprint',
      'afterprint'
    ];
    
    const urlLower = url.toLowerCase();
    const hasDangerousPattern = dangerousPatterns.some(pattern => urlLower.includes(pattern));
    if (hasDangerousPattern) {
      console.warn('Blocked potentially malicious URL:', url);
      return false;
    }
    
    // Basic URL format check for image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const hasImageExtension = imageExtensions.some(ext => urlLower.includes(ext)) || urlLower.includes('image');
    
    if (!hasImageExtension) return false;
    
    // Additional validation: try to load the image to verify it's actually an image
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
        
        // Timeout after 5 seconds
        setTimeout(() => resolve(false), 5000);
      });
    } catch (error) {
      console.warn('Error validating image URL:', error);
      return false;
    }
  };

  // Debounce utility function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Real-time URL validation for image URLs with debouncing
  const validateImageUrlRealTime = useCallback(
    debounce(async (url) => {
      if (!url.trim()) {
        setUrlValidationError('');
        return;
      }

      setIsValidatingUrl(true);
      setUrlValidationError('');

      try {
        const isValid = await isValidImageUrl(url);
        if (!isValid) {
          setUrlValidationError('Invalid image URL. Please check that the URL points to a valid image file.');
        }
      } catch (error) {
        console.error('Error validating image URL:', error);
        setUrlValidationError('Error validating image URL. Please try again.');
      } finally {
        setIsValidatingUrl(false);
      }
    }, 1000), // 1 second debounce
    []
  );

  // Real-time URL validation for video URLs with debouncing
  const validateVideoUrlRealTime = useCallback(
    debounce(async (url) => {
      if (!url.trim()) {
        setVideoUrlValidationError('');
        return;
      }

      setIsValidatingVideoUrl(true);
      setVideoUrlValidationError('');

      try {
        const isValid = await isValidVideoUrl(url);
        if (!isValid) {
          // Check if URL was blocked for security reasons
          const urlLower = url.toLowerCase();
          const hasDangerousPattern = [
            'javascript:', 'vbscript:', 'data:', 'file:', 'blob:',
            '<script', '</script>', 'onload=', 'onerror=', 'onclick=',
            'eval(', 'Function(', 'document.', 'window.', 'alert('
          ].some(pattern => urlLower.includes(pattern));
          
          // Check if it's an audio file instead of video
          const audioExtensions = ['.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a', '.wma'];
          const hasAudioExtension = audioExtensions.some(ext => urlLower.includes(ext));
          
          if (hasDangerousPattern) {
            setVideoUrlValidationError('URL blocked for security reasons. Please use a safe video URL.');
          } else if (hasAudioExtension) {
            setVideoUrlValidationError('Audio files are not supported. Please use a video URL (MP4, WebM, etc.) or video platform (YouTube, Vimeo).');
          } else {
            setVideoUrlValidationError('Invalid video URL. Please use a video platform (YouTube, Vimeo) or direct video file (MP4, WebM, etc.).');
          }
        }
      } catch (error) {
        console.error('Error validating video URL:', error);
        setVideoUrlValidationError('Error validating video URL. Please try again.');
      } finally {
        setIsValidatingVideoUrl(false);
      }
    }, 1500), // 1.5 second debounce (videos take longer to validate)
    []
  );

  // Test function for URL validation (can be called from console for testing)
  const testImageUrlValidation = async (url) => {
    console.log(`Testing URL: ${url}`);
    const isValid = await isValidImageUrl(url);
    console.log(`Result: ${isValid ? 'Valid' : 'Invalid'}`);
    return isValid;
  };

  // Make test function available globally for debugging
  if (typeof window !== 'undefined') {
    window.testImageUrlValidation = testImageUrlValidation;
  }

  // Helper function to detect video platform and get embed URL
  const getVideoEmbedInfo = (url) => {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    
    // YouTube detection and embed URL generation
    if (urlLower.includes('youtube.com/watch') || urlLower.includes('youtu.be/') || urlLower.includes('youtube.com/shorts')) {
      let videoId = '';
      
      if (urlLower.includes('youtube.com/watch')) {
        const match = url.match(/[?&]v=([^&]+)/);
        videoId = match ? match[1] : '';
      } else if (urlLower.includes('youtu.be/')) {
        const match = url.match(/youtu\.be\/([^?&]+)/);
        videoId = match ? match[1] : '';
      } else if (urlLower.includes('youtube.com/shorts')) { // 👈 NEW: Shorts logic
        const match = url.match(/shorts\/([^?&/]+)/);
        videoId = match ? match[1] : '';
      }
      
      if (videoId) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          originalUrl: url
        };
      }
    }
    
    // Vimeo detection and embed URL generation
    if (urlLower.includes('vimeo.com/')) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      const videoId = match ? match[1] : '';
      
      if (videoId) {
        return {
          platform: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${videoId}`,
          originalUrl: url
        };
      }
    }
    
    // Dailymotion detection and embed URL generation
    if (urlLower.includes('dailymotion.com/video/')) {
      const match = url.match(/dailymotion\.com\/video\/([^?&]+)/);
      const videoId = match ? match[1] : '';
      
      if (videoId) {
        return {
          platform: 'dailymotion',
          embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
          originalUrl: url
        };
      }
    }
    
    // Twitch detection and embed URL generation
    if (urlLower.includes('twitch.tv/videos/')) {
      const match = url.match(/twitch\.tv\/videos\/(\d+)/);
      const videoId = match ? match[1] : '';
      
      if (videoId) {
        return {
          platform: 'twitch',
          embedUrl: `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`,
          originalUrl: url
        };
      }
    }
    
    // For direct video files, return null to use video element
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv'];
    const hasVideoExtension = videoExtensions.some(ext => urlLower.includes(ext));
    
    if (hasVideoExtension) {
      return {
        platform: 'direct',
        embedUrl: url,
        originalUrl: url
      };
    }
    
    return null;
  };

  const isValidVideoUrl = async (url) => {
    if (!isValidUrl(url)) return false;
    
    // Security check: Block script injection patterns (same as image validation)
    const dangerousPatterns = [
      '<script', '</script>', 'javascript:', 'vbscript:', 'data:', 'file:', 'blob:',
      'onload=', 'onerror=', 'onclick=', 'onmouseover=', 'onfocus=', 'onblur=',
      'onchange=', 'onsubmit=', 'onreset=', 'onselect=', 'onkeydown=', 'onkeyup=',
      'onkeypress=', 'onmousedown=', 'onmouseup=', 'onmousemove=', 'onmouseout=',
      'onmouseenter=', 'onmouseleave=', 'oncontextmenu=', 'onresize=', 'onscroll=',
      'onunload=', 'onbeforeunload=', 'onabort=', 'eval(', 'Function(', 'setTimeout(',
      'setInterval(', 'document.', 'window.', 'alert(', 'confirm(', 'prompt(',
      'console.', 'localStorage.', 'sessionStorage.', 'cookie', 'document.cookie',
      'innerHTML', 'outerHTML', 'insertAdjacentHTML', 'createElement', 'appendChild',
      'removeChild', 'replaceChild', 'insertBefore', 'cloneNode', 'importNode',
      'adoptNode', 'normalize', 'splitText', 'deleteData', 'insertData', 'replaceData',
      'substringData', 'getAttribute', 'setAttribute', 'removeAttribute', 'hasAttribute',
      'getAttributeNode', 'setAttributeNode', 'removeAttributeNode', 'getElementsByTagName',
      'getElementsByClassName', 'getElementById', 'querySelector', 'querySelectorAll',
      'getComputedStyle', 'matchMedia', 'addEventListener', 'removeEventListener',
      'dispatchEvent', 'createEvent', 'createEventObject', 'attachEvent', 'detachEvent',
      'fireEvent', 'execCommand', 'queryCommandEnabled', 'queryCommandIndeterm',
      'queryCommandState', 'queryCommandSupported', 'queryCommandValue', 'write(',
      'writeln(', 'open(', 'close(', 'clear(', 'focus(', 'blur(', 'print(', 'stop(',
      'forward(', 'back(', 'home(', 'search(', 'reload(', 'replace(', 'assign(',
      'navigate(', 'go(', 'pushState(', 'replaceState(', 'popstate', 'hashchange',
      'beforeunload', 'unload', 'load', 'error', 'abort', 'resize', 'scroll',
      'focus', 'blur', 'change', 'submit', 'reset', 'select', 'keydown', 'keyup',
      'keypress', 'mousedown', 'mouseup', 'mousemove', 'mouseout', 'mouseover',
      'mouseenter', 'mouseleave', 'contextmenu', 'dblclick', 'click', 'drag',
      'dragend', 'dragenter', 'dragexit', 'dragleave', 'dragover', 'dragstart',
      'drop', 'touchstart', 'touchend', 'touchmove', 'touchcancel', 'gesturestart',
      'gesturechange', 'gestureend', 'orientationchange', 'devicemotion',
      'deviceorientation', 'deviceproximity', 'userproximity', 'visibilitychange',
      'storage', 'message', 'messageerror', 'beforeprint', 'afterprint'
    ];
    
    const urlLower = url.toLowerCase();
    const hasDangerousPattern = dangerousPatterns.some(pattern => urlLower.includes(pattern));
    if (hasDangerousPattern) {
      console.warn('Blocked potentially malicious video URL:', url);
      return false;
    }
    
    // Check for video platforms (YouTube, Vimeo, etc.)
    const videoPlatforms = [
      'youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com', 'twitch.tv',
      'facebook.com/watch', 'instagram.com/p/', 'tiktok.com', 'vine.co'
    ];
    
    const isVideoPlatform = videoPlatforms.some(platform => urlLower.includes(platform));
    if (isVideoPlatform) {
      return true; // Trust video platforms
    }
    
    // Check for video file extensions
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv'];
    const hasVideoExtension = videoExtensions.some(ext => urlLower.includes(ext));
    
    if (!hasVideoExtension) {
      return false;
    }
    
    // For direct video files, try to validate the content
    try {
      return await new Promise((resolve) => {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          // Check if it's actually a video by looking at duration
          if (video.duration > 0) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        video.onerror = () => resolve(false);
        video.src = url;
        
        // Timeout after 10 seconds (videos take longer to load than images)
        setTimeout(() => resolve(false), 10000);
      });
    } catch (error) {
      console.warn('Error validating video URL:', error);
      return false;
    }
  };

  // Legacy regex-based validator retained for backward compatibility with
  // pre-existing Contact rows that have no country_code. Per-country validation
  // for new entries comes from validatePhone() in countryDialCodes.js.
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }


    // For Contact type, validate at least one of phone / whatsapp is present
    // and each present number matches the per-country digit rules.
    if (formData.type === 'Contact') {
      const phoneDigits = stripNonDigits(formData.phone_number);
      const waDigits    = stripNonDigits(formData.whatsapp_number);

      if (!phoneDigits && !waDigits) {
        toast.error('Please enter a phone number or WhatsApp number');
        return;
      }

      if (phoneDigits && !validatePhone(formData.phone_country_code, phoneDigits)) {
        const c = findByCode(formData.phone_country_code);
        toast.error(`Please enter a valid phone number for ${c ? c.name : 'the selected phone country'}`);
        return;
      }

      if (waDigits && !validatePhone(formData.whatsapp_country_code, waDigits)) {
        const c = findByCode(formData.whatsapp_country_code);
        toast.error(`Please enter a valid WhatsApp number for ${c ? c.name : 'the selected WhatsApp country'}`);
        return;
      }
    }

    // For Get Directions type, address is required
    if (formData.type === 'Get Directions') {
      if (!formData.url.trim()) {
        toast.error('Please enter an address');
        return;
      }
    }

    // For file types, check if file is selected (except Image and Video which can be URL)
    if (['PDF'].includes(formData.type) && !file) {
      toast.error(`Please select a ${formData.type} file`);
      return;
    }

    // For Image type, check if either file is selected or URL is provided
    if (formData.type === 'Image') {
      if (imageInputType === 'upload' && !file) {
        toast.error('Please select an image file or switch to URL input');
        return;
      }
      if (imageInputType === 'url') {
        if (!formData.url.trim()) {
          toast.error('Please enter an image URL or switch to file upload');
          return;
        }
        
        // Show loading message for image URL validation
        toast.info('Validating image URL...', { autoClose: 2000 });
        
        try {
          const isValid = await isValidImageUrl(formData.url);
          if (!isValid) {
            // Check if URL was blocked for security reasons
            const urlLower = formData.url.toLowerCase();
            const hasDangerousPattern = [
              'javascript:', 'vbscript:', 'data:', 'file:', 'blob:',
              '<script', '</script>', 'onload=', 'onerror=', 'onclick=',
              'eval(', 'Function(', 'document.', 'window.', 'alert('
            ].some(pattern => urlLower.includes(pattern));
            
            if (hasDangerousPattern) {
              toast.error('URL blocked for security reasons. Please use a safe image URL.');
            } else {
              toast.error('Invalid image URL. Please check that the URL points to a valid image file.');
            }
            return;
          }
        } catch (error) {
          console.error('Error validating image URL:', error);
          toast.error('Error validating image URL. Please try again.');
          return;
        }
      }
    }

    // For Video type, check if URL is provided
    if (formData.type === 'Video') {
      if (!formData.url.trim()) {
        toast.error('Please enter a video URL');
        return;
      }
      
      // Show loading message for video URL validation
      toast.info('Validating video URL...', { autoClose: 3000 });
      
      try {
        const isValid = await isValidVideoUrl(formData.url);
        if (!isValid) {
          // Check if URL was blocked for security reasons
          const urlLower = formData.url.toLowerCase();
          const hasDangerousPattern = [
            'javascript:', 'vbscript:', 'data:', 'file:', 'blob:',
            '<script', '</script>', 'onload=', 'onerror=', 'onclick=',
            'eval(', 'Function(', 'document.', 'window.', 'alert('
          ].some(pattern => urlLower.includes(pattern));
          
          // Check if it's an audio file instead of video
          const audioExtensions = ['.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a', '.wma'];
          const hasAudioExtension = audioExtensions.some(ext => urlLower.includes(ext));
          
          if (hasDangerousPattern) {
            toast.error('URL blocked for security reasons. Please use a safe video URL.');
          } else if (hasAudioExtension) {
            toast.error('Audio files are not supported. Please use a video URL (MP4, WebM, etc.) or video platform (YouTube, Vimeo).');
          } else {
            toast.error('Invalid video URL. Please use a video platform (YouTube, Vimeo) or direct video file (MP4, WebM, etc.).');
          }
          return;
        }
      } catch (error) {
        console.error('Error validating video URL:', error);
        toast.error('Error validating video URL. Please try again.');
        return;
      }
    }

    // Check for duplicate assets on client side (using already loaded assets)
    const trimmedTitle = formData.title.trim();
    const trimmedDescription = formData.description.trim();
    
    console.log('Checking for duplicates with:', {
      title: trimmedTitle,
      description: trimmedDescription,
      type: formData.type
    });
    
    // Check against already loaded assets
    const isDuplicate = assets.some(asset => 
      asset.title === trimmedTitle && 
      asset.description === trimmedDescription &&
      asset.type === formData.type &&
      !asset.is_deleted
    );
    
    console.log('Client-side duplicate check result:', isDuplicate);
    console.log('Available assets for comparison:', assets.length);
    
    if (isDuplicate) {
      console.log('Duplicate found on client side, showing error message');
      toast.error('An asset with the same title and description already exists ! Try again');
      return;
    } else {
      console.log('No duplicate found on client side, proceeding with submission');
    }

    try {
      setLoading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('shop_domain', shop_domain);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);

      if (formData.type === 'Contact') {
        const phoneDigits = stripNonDigits(formData.phone_number);
        const waDigits    = stripNonDigits(formData.whatsapp_number);
        const phoneCountry = findByCode(formData.phone_country_code);
        const waCountry    = findByCode(formData.whatsapp_country_code);

        // New independent fields.
        if (phoneDigits) {
          formDataToSend.append('phone_country_code', formData.phone_country_code);
          if (phoneCountry) formDataToSend.append('phone_dial_code', phoneCountry.dial);
          formDataToSend.append('phone_number', phoneDigits);
        }
        if (waDigits) {
          formDataToSend.append('whatsapp_country_code', formData.whatsapp_country_code);
          if (waCountry) formDataToSend.append('whatsapp_dial_code', waCountry.dial);
          formDataToSend.append('whatsapp_number', waDigits);
        }

        // Legacy mirror — prefer phone, else whatsapp — so older readers
        // (template engine, AssetGroups, Forms.jsx) keep working until they're
        // updated to read the new fields directly.
        const legacyDigits  = phoneDigits || waDigits;
        const legacyCountry = phoneDigits ? formData.phone_country_code : formData.whatsapp_country_code;
        const legacyDial    = phoneDigits ? (phoneCountry && phoneCountry.dial) : (waCountry && waCountry.dial);
        formDataToSend.append('url', legacyDigits);
        formDataToSend.append('country_code', legacyCountry);
        if (legacyDial) formDataToSend.append('dial_code', legacyDial);
      } else {
        formDataToSend.append('url', formData.url);
      }

      // For Image type, add URL if using URL input
      if (formData.type === 'Image' && imageInputType === 'url') {
        formDataToSend.append('image_url', formData.url);
      }

      // For Video type, add URL
      if (formData.type === 'Video') {
        formDataToSend.append('video_url', formData.url);
        console.log('Frontend - Video URL being sent:', formData.url);
      }
      
      if (file) {
        formDataToSend.append('file', file);
      }

      const response = await axios.post(`${app_url}/api/assets/create`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Asset saved successfully!');
        setFormData({
          type: 'Video', title: '', description: '', url: '', country_code: 'US',
          phone_country_code: 'US', phone_number: '',
          whatsapp_country_code: 'US', whatsapp_number: ''
        });
        setFile(null);
        setImageInputType('upload'); // Reset to upload mode
        // Reset file input if it exists
        const fileInput = document.getElementById('fileInput');
        if (fileInput) {
          fileInput.value = '';
        }
        loadAssets();
      } else {
        toast.error(response.data.message || 'Failed to save asset');
      }
    } catch (error) {
      console.error('Error saving asset:', error);
      toast.error('Error saving asset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle asset deletion
  const handleDelete = async (assetId) => {
    try {
      setLoading(true);
      console.log('Deleting asset with ID:', assetId);
      console.log('Using shop_domain:', shop_domain);
      
      const response = await axios.post(`${app_url}/api/assets/delete`, {
        shop_domain: shop_domain,
        asset_id: assetId
      });

      console.log('Delete API response:', response.data);

      if (response.data.success) {
        toast.success('Asset deleted successfully!');
        loadAssets();
      } else {
        console.error('Delete failed:', response.data.message);
        toast.error(response.data.message || 'Failed to delete asset');
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Error deleting asset. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setAssetToDelete(null);
    }
  };

  // Confirm delete action
  const confirmDelete = (asset) => {
    setAssetToDelete(asset);
    setShowDeleteModal(true);
  };

  // Get display URL for Link and Contact types
  const getDisplayUrl = (asset) => {
    if (asset.type === 'Link' || asset.type === 'Contact') {
      return asset.url || asset.description;
    }
    return null;
  };

  // Get file preview URL for uploaded files
  const getFilePreviewUrl = (asset) => {
    if (['PDF', 'Video', 'Image', 'Audio'].includes(asset.type) && asset.fileName) {
      return `${app_url}/api/assets/file/${asset._id}?shop_domain=${shop_domain}`;
    }
    return null;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Filter assets based on type
  const filteredAssets = assets.filter(asset => {
    if (filterType === 'All') return true;
    return asset.type === filterType;
  });

  // Get unique asset types for filter dropdown
  const uniqueAssetTypes = ['All', ...new Set(assets.map(asset => asset.type))];

  // Handle preview asset
  const handlePreview = (asset) => {
    console.log('=== ASSET PREVIEW DEBUG ===');
    console.log('Asset title:', asset.title);
    console.log('Asset type:', asset.type);
    console.log('Has fileName:', !!asset.fileName, asset.fileName);
    console.log('Has url:', !!asset.url, asset.url);
    console.log('Has originalName:', !!asset.originalName, asset.originalName);
    console.log('File preview URL:', getFilePreviewUrl(asset));
    console.log('Complete asset data:', asset);
    console.log('========================');
    setPreviewAsset(asset);
    setShowPreviewModal(true);
  };

  // Close preview modal
  const closePreview = () => {
    setShowPreviewModal(false);
    setPreviewAsset(null);
  };

  return (
    <div className={isInSettingsContainer ? '' : 'main-container'}>
      {!isInSettingsContainer && (
        <div className='main-title mt-4'>
          <h2>Assets</h2>
        </div>
      )}
      

      <Container fluid className="p-0">
        <Row>
          <Col>
            <div className="main-accordion-body mt-4 p-2">
            <Accordion activeKey={activeKeys[0] || null} onSelect={(k) => setActiveKeys(k ? [k] : [])} className="block-repository-accordion">
              {/* Add Assets Accordion */}
              <Accordion.Item eventKey="add-assets">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="sxi-accordion-header-with-icon">
                      {/* <div className="sxi-accordion-header-icon" aria-hidden="true">
                        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g filter="url(#filter_add_assets)">
                            <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges"/>
                            <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges"/>
                            <path d="M27 16V38M16 27H38" stroke="#2056C1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                          <defs>
                            <filter id="filter_add_assets" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                              <feOffset dx="2" dy="2"/>
                              <feGaussianBlur stdDeviation="2"/>
                              <feComposite in2="hardAlpha" operator="out"/>
                              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
                              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                            </filter>
                          </defs>
                        </svg>
                      </div> */}
                      <span>Add Assets</span>
                    </div>
                    <span className={`campaign-accordion-chevron ${activeKeys.includes('add-assets') ? 'open' : ''}`} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Type</Form.Label>
                              <Form.Select
                                name="type"
                                value={formData.type}
                                onChange={handleInputChange}
                                required
                              >
                                {assetTypes.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Title</Form.Label>
                              <Form.Control
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter asset title"
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter asset description"
                            required
                          />
                        </Form.Group>

                        {formData.type === 'Contact' && (() => {
                          const phoneCountry = findByCode(formData.phone_country_code);
                          const phoneDigits  = stripNonDigits(formData.phone_number);
                          const phoneValid   = phoneCountry ? validatePhone(formData.phone_country_code, phoneDigits) : false;

                          const waCountry = findByCode(formData.whatsapp_country_code);
                          const waDigits  = stripNonDigits(formData.whatsapp_number);
                          const waValid   = waCountry ? validatePhone(formData.whatsapp_country_code, waDigits) : false;

                          const digitHint = (c) => c
                            ? `${c.min === c.max ? c.min : `${c.min}-${c.max}`} digits`
                            : '';

                          return (
                            <>
                              <Form.Group className="mb-3">
                                <Form.Label>Phone</Form.Label>
                                <Row className="g-2">
                                  <Col xs={5} md={4}>
                                    <Form.Select
                                      value={formData.phone_country_code}
                                      onChange={(e) => setFormData(prev => ({ ...prev, phone_country_code: e.target.value }))}
                                      aria-label="Phone country"
                                    >
                                      {COUNTRY_DIAL_CODES.map(c => (
                                        <option key={c.code} value={c.code}>{c.dial} — {c.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                  <Col xs={7} md={8}>
                                    <Form.Control
                                      type="tel"
                                      inputMode="numeric"
                                      autoComplete="tel-national"
                                      name="phone_number"
                                      value={formData.phone_number}
                                      onChange={(e) => setFormData(prev => ({ ...prev, phone_number: stripNonDigits(e.target.value) }))}
                                      placeholder={phoneCountry ? digitHint(phoneCountry) : 'Phone number'}
                                      isInvalid={!!phoneDigits && !phoneValid}
                                      isValid={!!phoneDigits && phoneValid}
                                    />
                                  </Col>
                                </Row>
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>WhatsApp</Form.Label>
                                <Row className="g-2">
                                  <Col xs={5} md={4}>
                                    <Form.Select
                                      value={formData.whatsapp_country_code}
                                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_country_code: e.target.value }))}
                                      aria-label="WhatsApp country"
                                    >
                                      {COUNTRY_DIAL_CODES.map(c => (
                                        <option key={c.code} value={c.code}>{c.dial} — {c.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                  <Col xs={7} md={8}>
                                    <Form.Control
                                      type="tel"
                                      inputMode="numeric"
                                      autoComplete="tel-national"
                                      name="whatsapp_number"
                                      value={formData.whatsapp_number}
                                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_number: stripNonDigits(e.target.value) }))}
                                      placeholder={waCountry ? digitHint(waCountry) : 'WhatsApp number'}
                                      isInvalid={!!waDigits && !waValid}
                                      isValid={!!waDigits && waValid}
                                    />
                                  </Col>
                                </Row>
                                <Form.Text className="text-muted">
                                  Digits only — country code is added automatically. Enter Phone, WhatsApp, or both.
                                </Form.Text>
                              </Form.Group>
                            </>
                          );
                        })()}

                        {formData.type === 'Get Directions' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Maps Location (address)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              name="url"
                              value={formData.url}
                              onChange={handleUrlChange}
                              placeholder="e.g. 1600 Amphitheatre Pkwy, Mountain View, CA"
                              required
                            />
                            <Form.Text className="text-muted">
                              Plain-text address. Consumer pages turn it into a Google Maps directions link.
                            </Form.Text>
                          </Form.Group>
                        )}

                        {formData.type === 'Image' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Image Input Method</Form.Label>
                            <div className="mb-3">
                              <Form.Check
                                type="radio"
                                label="Upload Image File"
                                name="imageInputType"
                                id="uploadImage"
                                value="upload"
                                checked={imageInputType === 'upload'}
                                onChange={(e) => handleImageInputTypeChange(e.target.value)}
                                inline
                              />
                              <Form.Check
                                type="radio"
                                label="Image URL"
                                name="imageInputType"
                                id="urlImage"
                                value="url"
                                checked={imageInputType === 'url'}
                                onChange={(e) => handleImageInputTypeChange(e.target.value)}
                                inline
                              />
                            </div>
                            
                            {imageInputType === 'upload' ? (
                              <>
                                <Form.Control
                                  id="fileInput"
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                  required
                                />
                                <Form.Text className="text-muted">
                                  Upload an image file (JPG, PNG, GIF, etc.) (max 10MB)
                                </Form.Text>
                                {file && (
                                  <div className="mt-2">
                                    <small className="text-success">
                                      Selected: {file.name} ({formatFileSize(file.size)})
                                    </small>
                                    {file.size > 8 * 1024 * 1024 && (
                                      <div className="mt-1">
                                        <small className="text-warning">
                                          ⚠️ File size is close to the 10MB limit
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <Form.Control
                                  type="url"
                                  placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                                  value={formData.url}
                                  onChange={handleUrlChange}
                                  required
                                  isInvalid={!!urlValidationError}
                                />
                                <Form.Text className="text-muted">
                                  Enter a direct URL to an image file
                                </Form.Text>
                                {isValidatingUrl && (
                                  <div className="mt-2">
                                    <small className="text-info">
                                      <Spinner size="sm" className="me-1" />
                                      Validating image URL...
                                    </small>
                                  </div>
                                )}
                                {urlValidationError && (
                                  <div className="mt-2">
                                    <small className="text-danger">
                                      ⚠️ {urlValidationError}
                                    </small>
                                  </div>
                                )}
                              </>
                            )}
                          </Form.Group>
                        )}

                        {formData.type === 'Video' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Video URL</Form.Label>
                            <Form.Control
                              type="url"
                              placeholder="Enter video URL (YouTube, Vimeo, or direct video file)"
                              value={formData.url}
                              onChange={handleUrlChange}
                              required
                              isInvalid={!!videoUrlValidationError}
                            />
                            <Form.Text className="text-muted">
                              Enter a YouTube, Vimeo, or direct video file URL
                            </Form.Text>
                            {isValidatingVideoUrl && (
                              <div className="mt-2">
                                <small className="text-info">
                                  <Spinner size="sm" className="me-1" />
                                  Validating video URL...
                                </small>
                              </div>
                            )}
                            {videoUrlValidationError && (
                              <div className="mt-2">
                                <small className="text-danger">
                                  ⚠️ {videoUrlValidationError}
                                </small>
                              </div>
                            )}
                          </Form.Group>
                        )}

                        {formData.type === 'PDF' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Upload PDF</Form.Label>
                            <Form.Control
                              id="fileInput"
                              type="file"
                              accept=".pdf"
                              onChange={handleFileChange}
                              required
                            />
                            <Form.Text className="text-muted">
                              Upload a PDF file (max 10MB)
                            </Form.Text>
                            {file && (
                              <div className="mt-2">
                                <small className="text-success">
                                  Selected: {file.name} ({formatFileSize(file.size)})
                                </small>
                                {file.size > 8 * 1024 * 1024 && (
                                  <div className="mt-1">
                                    <small className="text-warning">
                                      ⚠️ File size is close to the 10MB limit
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </Form.Group>
                        )}

                        <Button
                          type="submit"
                          disabled={loading}
                          className="sxi_primary_blue_btn btn-height me-2"
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            'Save Asset'
                          )}
                        </Button>
                      </Form>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              {/* View Assets Accordion */}
              <Accordion.Item eventKey="view-assets">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="sxi-accordion-header-with-icon">
                      {/* <div className="sxi-accordion-header-icon" aria-hidden="true">
                        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g filter="url(#filter_view_assets)">
                            <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges"/>
                            <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges"/>
                            <path d="M27 18C20 18 14 27 14 27C14 27 20 36 27 36C34 36 40 27 40 27C40 27 34 18 27 18Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="27" cy="27" r="3.5" stroke="#2056C1" strokeWidth="2"/>
                          </g>
                          <defs>
                            <filter id="filter_view_assets" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                              <feOffset dx="2" dy="2"/>
                              <feGaussianBlur stdDeviation="2"/>
                              <feComposite in2="hardAlpha" operator="out"/>
                              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
                              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                            </filter>
                          </defs>
                        </svg>
                      </div> */}
                      <span>View Assets</span>
                    </div>
                    <span className={`campaign-accordion-chevron ${activeKeys.includes('view-assets') ? 'open' : ''}`} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div>
                      {/* Filter Section */}
                      <div className="mb-3">
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Filter by Type:</Form.Label>
                              <Form.Select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                              >
                                {uniqueAssetTypes.map(type => (
                                  <option key={type} value={type}>
                                    {type} {type !== 'All' && `(${assets.filter(asset => asset.type === type).length})`}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6} className="d-flex align-items-end">
                            <div className="text-muted">
                              Showing {filteredAssets.length} of {assets.length} assets
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {loading ? (
                        <div className="text-center">
                          <Spinner animation="border" />
                          <p className="mt-2">Loading assets...</p>
                        </div>
                      ) : filteredAssets.length === 0 ? (
                        <Alert variant="info">
                          {filterType === 'All' 
                            ? 'No assets found. Add your first asset using the "Add Assets" section above.'
                            : `No ${filterType} assets found. Try changing the filter or add a new ${filterType} asset.`
                          }
                        </Alert>
                      ) : (
                        <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                          <Table responsive className="mb-0">
                          <thead>
                            <tr>
                              <th className="channel_performance_header_cell">Type</th>
                              <th className="channel_performance_header_cell">Title</th>
                              <th className="channel_performance_header_cell">Description</th>
                              {/* <th className="channel_performance_header_cell">Size</th> */}
                              <th className="channel_performance_header_cell">Created</th>
                              <th className="channel_performance_header_cell text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAssets.map((asset, idx) => (
                              <tr key={asset._id} className={idx % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}>
                                <td className="channel_performance_table_cell">
                                
                                  <span className={`badge asset-type-badge bg-secondary 
                                  ${
                                    // asset.type === 'Link' ? 'bg-primary' :
                                    // asset.type === 'PDF' ? 'bg-danger' :
                                    // asset.type === 'Video' ? 'bg-warning' :
                                    // asset.type === 'Image' ? 'bg-success' :
                                    // asset.type === 'Audio' ? 'bg-info' :
                                    'bg-secondary'
                                  }`
                                  }>
                                    {asset.type}
                                  </span>
                                </td>
                                <td className="channel_performance_table_cell">{asset.title}</td>
                                <td className="channel_performance_table_cell">
                                  <div 
                                    className="text-truncate" 
                                    style={{ maxWidth: '200px' }}
                                    title={asset.description}
                                  >
                                    {asset.description}
                                  </div>
                                </td>
                                {/* <td className="channel_performance_table_cell">
                                  {asset.fileSize ? formatFileSize(asset.fileSize) : '-'}
                                </td> */}
                                <td className="channel_performance_table_cell">
                                  {new Date(asset.createdAt).toLocaleDateString()}
                                </td>
                                <td className="channel_performance_table_cell">
                                  <div className="text-center">
                                    <button 
                                      className="btn btn-sm btn-link p-0 me-3" 
                                      onClick={() => handlePreview(asset)}
                                      disabled={loading}
                                      title="Preview Asset" 
                                      style={{ color: '#000' }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
                                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                                      </svg>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-link p-0"
                                      onClick={() => confirmDelete(asset)}
                                      disabled={loading}
                                      title="Delete Asset"
                                    >
                                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M23.3333 15V14.3334C23.3333 13.3999 23.3333 12.9332 23.1517 12.5767C22.9919 12.2631 22.7369 12.0081 22.4233 11.8483C22.0668 11.6667 21.6001 11.6667 20.6667 11.6667H19.3333C18.3999 11.6667 17.9332 11.6667 17.5767 11.8483C17.2631 12.0081 17.0081 12.2631 16.8483 12.5767C16.6667 12.9332 16.6667 13.3999 16.6667 14.3334V15M18.3333 19.5834V23.75M21.6667 19.5834V23.75M12.5 15H27.5M25.8333 15V24.3334C25.8333 25.7335 25.8333 26.4336 25.5608 26.9683C25.3212 27.4387 24.9387 27.8212 24.4683 28.0609C23.9335 28.3334 23.2335 28.3334 21.8333 28.3334H18.1667C16.7665 28.3334 16.0665 28.3334 15.5317 28.0609C15.0613 27.8212 14.6788 27.4387 14.4392 26.9683C14.1667 26.4336 14.1667 25.7335 14.1667 24.3334V15" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          </Table>
                        </div>
                      )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the asset "{assetToDelete?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button className="sxi_primary_blue_btn btn-height" onClick={() => handleDelete(assetToDelete?._id)}>
            Delete
          </Button>
          <button className="back-button btn-height ms-2" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={closePreview} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            👁️ Asset Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewAsset && (
            <div>
              <Row className="mb-3">
                <Col md={3}>
                  <strong>Type:</strong>
                </Col>
                <Col md={9}>
                  <span className={`badge asset-type-badge bg-secondary`}>
                    {previewAsset.type}
                  </span>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={3}>
                  <strong>Title:</strong>
                </Col>
                <Col md={9}>
                  {previewAsset.title}
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={3}>
                  <strong>Description:</strong>
                </Col>
                <Col md={9}>
                  <div className="border p-3 rounded bg-light">
                    {previewAsset.description}
                  </div>
                </Col>
              </Row>

              {previewAsset.type === 'Contact' && (() => {
                // New-field-preferred, legacy fallback. Render Phone and
                // WhatsApp as separate clickable rows; skip whichever is empty.
                const phoneDial = previewAsset.phone_dial_code || previewAsset.dial_code || '';
                const phoneNum  = previewAsset.phone_number    || previewAsset.url       || '';
                const waDial    = previewAsset.whatsapp_dial_code || '';
                const waNum     = previewAsset.whatsapp_number    || '';
                const waDigits  = `${waDial.replace(/\D/g, '')}${waNum.replace(/\D/g, '')}`;

                return (
                  <>
                    {phoneNum && (
                      <Row className="mb-3">
                        <Col md={3}><strong>Phone:</strong></Col>
                        <Col md={9}>
                          <a href={`tel:${phoneDial}${phoneNum}`} className="text-decoration-none">
                            📞 {phoneDial ? `${phoneDial} ${phoneNum}` : phoneNum}
                          </a>
                        </Col>
                      </Row>
                    )}
                    {waNum && (
                      <Row className="mb-3">
                        <Col md={3}><strong>WhatsApp:</strong></Col>
                        <Col md={9}>
                          <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            💬 {waDial ? `${waDial} ${waNum}` : waNum}
                          </a>
                        </Col>
                      </Row>
                    )}
                  </>
                );
              })()}

              {previewAsset.url && previewAsset.type !== 'Contact' && (
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>
                      {previewAsset.type === 'Get Directions' ? 'Address:' : 'URL/Phone:'}
                    </strong>
                  </Col>
                  <Col md={9}>
                    {previewAsset.type === 'Link' ? (
                      <a href={previewAsset.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                        🔗 {previewAsset.url}
                      </a>
                    ) : previewAsset.type === 'Get Directions' ? (
                      <>
                        <div className="mb-2">🗺️ {previewAsset.url}</div>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(previewAsset.url)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Open in Google Maps ↗
                        </a>
                      </>
                    ) : (
                      previewAsset.url
                    )}
                  </Col>
                </Row>
              )}

              {previewAsset.fileName && (
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>File:</strong>
                  </Col>
                  <Col md={9}>
                    <div>
                      📄 {previewAsset.originalName}
                      {previewAsset.fileSize && (
                        <small className="text-muted ms-2">
                          ({formatFileSize(previewAsset.fileSize)})
                        </small>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {previewAsset.type === 'Image' && (
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>Image:</strong>
                  </Col>
                  <Col md={9}>
                    <div>
                      {previewAsset.fileName ? (
                        <>
                          <img 
                            src={getFilePreviewUrl(previewAsset)} 
                            alt={previewAsset.title}
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                            className="rounded me-2"
                            onError={(e) => {
                              console.error('Image failed to load:', getFilePreviewUrl(previewAsset));
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none' }} className="text-muted">
                            <i className="fas fa-image me-1"></i>
                            Image failed to load: {getFilePreviewUrl(previewAsset)}
                          </div>
                        </>
                      ) : previewAsset.url ? (
                        <>
                          <img 
                            src={previewAsset.url} 
                            alt={previewAsset.title}
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                            className="rounded me-2"
                            onError={(e) => {
                              console.error('Image URL failed to load:', previewAsset.url);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none' }} className="text-muted">
                            <i className="fas fa-image me-1"></i>
                            Image URL (Failed to load): {previewAsset.url}
                          </div>
                          <div>
                            <a 
                              href={previewAsset.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              🔗 {previewAsset.url}
                            </a>
                          </div>
                        </>
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-image me-1"></i>
                          No image data available
                          <br />
                          <small>
                            Title: {previewAsset.title}<br />
                            fileName: {previewAsset.fileName || 'null'}<br />
                            url: {previewAsset.url || 'null'}<br />
                            originalName: {previewAsset.originalName || 'null'}
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {previewAsset.type === 'Video' && (
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>Video:</strong>
                  </Col>
                  <Col md={9}>
                    <div>
                      {previewAsset.fileName ? (
                        <>
                          <video 
                            src={getFilePreviewUrl(previewAsset)} 
                            style={{ maxWidth: '300px', maxHeight: '200px' }}
                            className="rounded me-2"
                            controls
                          />
                          <small className="text-muted d-block">{previewAsset.originalName}</small>
                        </>
                      ) : previewAsset.url ? (
                        <>
                          {(() => {
                            const embedInfo = getVideoEmbedInfo(previewAsset.url);
                            
                            if (embedInfo && embedInfo.platform !== 'direct') {
                              // Use iframe for video platforms (YouTube, Vimeo, etc.)
                              return (
                                <>
                                  <iframe
                                    src={embedInfo.embedUrl}
                                    width="300"
                                    height="200"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="rounded me-2"
                                    title={`${embedInfo.platform} video`}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div style={{ display: 'none' }} className="text-muted">
                                    <i className="fas fa-video me-1"></i>
                                    Video URL (Failed to load)
                                  </div>
                                  <div className="mt-2">
                                    <a 
                                      href={embedInfo.originalUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                    >
                                      🔗 {embedInfo.originalUrl}
                                    </a>
                                  </div>
                                </>
                              );
                            } else {
                              // Use video element for direct video files
                              return (
                                <>
                                  <video 
                                    src={previewAsset.url} 
                                    style={{ maxWidth: '300px', maxHeight: '200px' }}
                                    className="rounded me-2"
                                    controls
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div style={{ display: 'none' }} className="text-muted">
                                    <i className="fas fa-video me-1"></i>
                                    Video URL (Failed to load)
                                  </div>
                                  <div className="mt-2">
                                    <a 
                                      href={previewAsset.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                    >
                                      🔗 {previewAsset.url}
                                    </a>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              )}

              {previewAsset.type === 'PDF' && (previewAsset.fileName || previewAsset.url) && (
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>PDF:</strong>
                  </Col>
                  <Col md={9}>
                    {previewAsset.fileName ? (
                      <div>
                        <a
                          href={getFilePreviewUrl(previewAsset)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          📄 {previewAsset.originalName}
                        </a>
                        {previewAsset.fileSize && (
                          <small className="text-muted ms-2">
                            ({formatFileSize(previewAsset.fileSize)})
                          </small>
                        )}
                        <div className="mt-2">
                          <iframe
                            src={getFilePreviewUrl(previewAsset)}
                            width="100%"
                            height="400px"
                            title="PDF Preview"
                            className="border rounded"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <a
                          href={previewAsset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          📄 {previewAsset.url.split('/').pop()?.split('?')[0] || 'Open PDF'}
                        </a>
                        <div className="mt-2">
                          <iframe
                            src={previewAsset.url}
                            width="100%"
                            height="400px"
                            title="PDF Preview"
                            className="border rounded"
                          />
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              )}

              <Row className="mb-3">
                <Col md={3}>
                  <strong>Created:</strong>
                </Col>
                <Col md={9}>
                  {new Date(previewAsset.createdAt).toLocaleString()}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlockRepository;
